import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAccount } from "@/contexts/AccountContext";
import { supabase } from "@/lib/supabase";
import { oxpayAPI } from "@/lib/oxpay";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { format, subDays } from "date-fns";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import CardSelectModal from "@/components/CardSelectModal";

export default function CardHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { oxpayUserId } = useAccount();
  const [cardsData, setCardsData] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardNumber, setCardNumber] = useState("All Card");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isCardSelectOpen, setIsCardSelectOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Date range management for 90-day periods
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(new Date());
  const [currentPeriodStart, setCurrentPeriodStart] = useState(
    subDays(new Date(), 89),
  );
  const [lastTransactionReached, setLastTransactionReached] = useState(false);
  const CUTOFF_DATE = new Date("2025-07-01");
  const txRequestSeqRef = useRef(0);

  const getEffectiveUserId = (card) =>
    oxpayUserId ||
    card?.oxpay_user_id ||
    card?.oxpayUserId ||
    card?.user_id ||
    cardsData?.[0]?.oxpay_user_id ||
    cardsData?.[0]?.oxpayUserId ||
    cardsData?.[0]?.user_id ||
    null;

  const resolveCardId = (card) => {
    if (!card) return null;
    const matched = cardsData.find(
      (item) => item?.id && card?.id && String(item.id) === String(card.id),
    );
    return (matched || card)?.oxpay_card_id || null;
  };

  // Fetch cards data from active_cards table
  useEffect(() => {
    const fetchCards = async () => {
      if (!user?.email) return;

      try {
        const { data, error } = await supabase
          .from("active_cards")
          .select("*")
          .eq("email", user.email)
          .order("created_at", { ascending: true });

        if (error) {
          console.error("Error fetching cards:", error);
          setCardsData([]);
          setIsInitialized(true);
          return;
        }

        if (data && data.length > 0) {
          setCardsData(data);
          setSelectedCard(data[0]);
          setCardNumber(
            data[0].card_number
              ? `****-****-****-${data[0].card_number.slice(-4)}`
              : "Card",
          );
        } else {
          setCardsData([]);
        }
        setIsInitialized(true);
      } catch (err) {
        console.error("Error:", err);
        setCardsData([]);
        setIsInitialized(true);
      }
    };

    fetchCards();
  }, [user]);

  // Fetch transactions function - receives date range as parameters
  const fetchTransactionsWithRange = async (
    page,
    periodStart,
    periodEnd,
    isLoadingMore = false,
    selectedCardOverride = undefined,
  ) => {
    const requestSeq = ++txRequestSeqRef.current;
    console.log("Fetching transactions for page:", page);
    const effectiveSelectedCard =
      selectedCardOverride === undefined ? selectedCard : selectedCardOverride;
    const userId = getEffectiveUserId(effectiveSelectedCard);
    if (!userId) {
      console.warn("Cannot fetch transactions: missing OxPay user id", {
        oxpayUserId,
        selectedCard: effectiveSelectedCard,
      });
      return { success: false, hasData: false };
    }

    try {
      setLoading(true);

      // Format dates as YYYYMMDD
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}${month}${day}`;
      };

      const dateRange = `${formatDate(periodStart)},${formatDate(periodEnd)}`;

      console.log("Date range:", dateRange);

      let response;
      if (!effectiveSelectedCard) {
        response = await oxpayAPI.getTransactions(userId, {
          page: page,
          limit: 50,
          date_range: dateRange,
        });
      } else {
        const cardId = resolveCardId(effectiveSelectedCard);

        if (!cardId) {
          if (requestSeq === txRequestSeqRef.current) {
            setTransactions([]);
          }
          console.warn(
            "Selected card has no OxPay card id:",
            effectiveSelectedCard,
          );
          return { success: true, hasData: false, isLastPage: true };
        }

        response = await oxpayAPI.getCardTransactions(userId, cardId, {
          page: page,
          limit: 50,
          date_range: dateRange,
        });
      }

      if (requestSeq !== txRequestSeqRef.current) {
        return { success: false, stale: true };
      }

      if (
        response?.body?.transactions &&
        Array.isArray(response.body.transactions)
      ) {
        const newTransactions = response.body.transactions;
        console.log(
          `Page ${page}: Received ${newTransactions.length} transactions`,
        );

        if (isLoadingMore) {
          setTransactions((prev) => [...prev, ...newTransactions]);
        } else {
          setTransactions(newTransactions);
        }

        const hasData = newTransactions.length > 0;
        const isLastPage = newTransactions.length < 50;

        return { success: true, hasData, isLastPage };
      } else {
        if (!isLoadingMore) {
          setTransactions([]);
        }
        return { success: true, hasData: false, isLastPage: true };
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
      if (requestSeq === txRequestSeqRef.current && !isLoadingMore) {
        setTransactions([]);
      }
      return { success: false, hasData: false };
    } finally {
      if (requestSeq === txRequestSeqRef.current) {
        setLoading(false);
      }
    }
  };

  // Fetch transactions when card changes
  useEffect(() => {
    if (!isInitialized) {
      console.log("Skipping transaction fetch:", {
        isInitialized,
        oxpayUserId,
      });
      return;
    }

    const today = new Date();
    const periodStart = subDays(today, 89); // 90일 기간 (시작일 포함)

    setCurrentPeriodEnd(today);
    setCurrentPeriodStart(periodStart);
    setCurrentPage(1);
    setTransactions([]);
    setHasMore(true);
    setLastTransactionReached(false);

    console.log(
      "Initial load triggered for card:",
      selectedCard?.card_number || "All",
      "cardsData.length:",
      cardsData.length,
    );

    fetchTransactionsWithRange(1, periodStart, today, false).then((result) => {
      if (result?.stale) return;
      if (result?.isLastPage) {
        setLastTransactionReached(true);
        setHasMore(periodStart > CUTOFF_DATE);
      }
    });
  }, [selectedCard, oxpayUserId, isInitialized]);

  const handleLoadMore = async () => {
    if (loading) return;

    if (lastTransactionReached) {
      if (currentPeriodStart <= CUTOFF_DATE) {
        console.log("Reached cutoff date (2025-07-01), no more data");
        setHasMore(false);
        return;
      }

      const newPeriodEnd = subDays(currentPeriodStart, 1);
      const newPeriodStart = subDays(newPeriodEnd, 89); // 90일 기간 (시작일 포함)

      console.log("Moving to previous 90-day period:", {
        from: newPeriodStart.toISOString(),
        to: newPeriodEnd.toISOString(),
      });

      setCurrentPeriodEnd(newPeriodEnd);
      setCurrentPeriodStart(newPeriodStart);
      setCurrentPage(1);
      setLastTransactionReached(false);

      const result = await fetchTransactionsWithRange(
        1,
        newPeriodStart,
        newPeriodEnd,
        true,
      );

      if (result.isLastPage) {
        setLastTransactionReached(true);
        // 데이터 유무와 관계없이 CUTOFF_DATE 이전이면 More 버튼 표시
        setHasMore(newPeriodStart > CUTOFF_DATE);
      }
    } else {
      const nextPage = currentPage + 1;
      console.log("Loading next page:", nextPage);
      setCurrentPage(nextPage);

      const result = await fetchTransactionsWithRange(
        nextPage,
        currentPeriodStart,
        currentPeriodEnd,
        true,
      );

      if (result.isLastPage) {
        setLastTransactionReached(true);
        // 데이터 유무와 관계없이 CUTOFF_DATE 이전이면 More 버튼 표시
        setHasMore(currentPeriodStart > CUTOFF_DATE);
      }
    }
  };

  const handleCardSelect = (card) => {
    if (card === null) {
      setSelectedCard(null);
      setCardNumber("All Card");
    } else {
      setSelectedCard(card);
      setCardNumber(
        card.card_number
          ? `****-****-****-${card.card_number.slice(-4)}`
          : "Card",
      );
    }
    // Fetch only in useEffect(selectedCard) to avoid duplicate in-flight requests
    // and stale responses from the previous card.
  };

  // Calculate total amount - Commented out
  // const totalAmount = transactions.reduce((sum, transaction) => {
  //   const amount = parseFloat(transaction.amount || 0);
  //   return sum + amount;
  // }, 0);

  return (
    <div>
      <Header showBack={true} onBackClick={() => navigate("/home")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Card Number Selector */}
        <Card className="mt-4">
          <CardContent className="p-3">
            <label className="block text-sm font-semibold text-foreground/70 mb-2">
              Select Card
            </label>
            <button
              onClick={() => setIsCardSelectOpen(true)}
              className="w-full h-12 flex items-center justify-between px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold"
            >
              <span>{cardNumber}</span>
              <span>〉</span>
            </button>
          </CardContent>
        </Card>

        {/* Total Spend Card - Commented out */}
        {/* <Card className="mt-4">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              Total Spend for the Period
            </p>
            <p className="text-right text-lg font-semibold text-foreground">
              ${totalAmount.toFixed(2)}
            </p>
          </CardContent>
        </Card> */}

        {/* Transactions List */}
        <Card className="mt-4">
          <CardContent className="p-2">
            {loading ? (
              <div className="p-8 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-muted-foreground">No Data</p>
              </div>
            ) : (
              transactions.map((transaction, index) => {
                // Format date - wallet transactions use 'date' field, card transactions use 'transaction_date'
                const dateStr =
                  transaction.date || transaction.transaction_date;
                const formattedDate = dateStr
                  ? format(new Date(dateStr), "MM.dd | HH:mm")
                  : "";

                // Get transaction name - wallet uses 'type' and 'description', card uses 'merchant_name'
                const transactionName =
                  transaction.merchant_name ||
                  transaction.type ||
                  transaction.description ||
                  "Transaction";

                // Format amount with indicator (debit/credit)
                const amount = parseFloat(transaction.amount || 0);
                const isDebit = transaction.indicator === "debit";
                const isZero = amount === 0;
                const amountDisplay = isDebit
                  ? `-$${amount.toFixed(2)}`
                  : `$${amount.toFixed(2)}`;

                return (
                  <div
                    key={`${transaction.id}-${index}`}
                    className="p-3 border-b last:border-b-0 border-border/20"
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-foreground/70">
                          {transactionName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formattedDate}
                        </p>
                      </div>
                      <div className="text-right space-y-2">
                        <p
                          className={`text-sm font-medium ${
                            isZero
                              ? "text-foreground/70"
                              : isDebit
                                ? "text-red-500"
                                : "text-green-500"
                          }`}
                        >
                          {amountDisplay}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {transaction.status || "Completed"}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* View More Button */}
        {hasMore && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              onClick={handleLoadMore}
              disabled={loading}
              className="w-full max-w-xs"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "More"
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Modals */}
      <CardSelectModal
        isOpen={isCardSelectOpen}
        onClose={() => setIsCardSelectOpen(false)}
        selectedCard={cardNumber}
        cards={cardsData}
        onSelect={(card) => {
          handleCardSelect(card);
          setIsCardSelectOpen(false);
        }}
      />

      <BottomBar />
    </div>
  );
}
